import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Every pin uses the same config so all three sections hand off identically.
// The pin *ends* exactly when the next section's top reaches the viewport top
// (via `endTrigger`), so there is no unpinned scroll between sections — the
// previous section hands off directly to the next. With `scrub: true` and
// `pinType: 'fixed'` the handoff is instantaneous and visually seamless.
const pinDefaults = {
    pin: true,
    pinType: 'fixed',
    pinSpacing: true,
    scrub: true,
    anticipatePin: 1,
    invalidateOnRefresh: true,
};

// Build an `end` descriptor: pin until the next section reaches the top; if
// there isn't one, fall back to a fixed distance (one viewport per step) so
// the last case still has room for its animation.
function endFor(nextSelector, fallbackSteps) {
    const next = nextSelector ? document.querySelector(nextSelector) : null;
    return next
        ? { endTrigger: next, end: 'top top' }
        : { end: () => '+=' + fallbackSteps * window.innerHeight };
}

// ─── CASE 1 ─────────────────────────────────────────────────────────────────
// Section pins; cards are stacked at the vertical centre. Each card fades out
// while the next one slides up from below and fades in — one step per viewport.
function initCase1() {
    const section = document.querySelector('.case-1');
    if (!section) return;

    const cards = gsap.utils.toArray('.case-1 .steps-container .step-item');
    if (cards.length < 2) return;

    gsap.set(cards, { yPercent: -50, top: '50%' });
    gsap.set(cards[0], { opacity: 1, y: 0 });
    gsap.set(cards.slice(1), { opacity: 0, y: () => window.innerHeight });

    const tl = gsap.timeline();
    for (let i = 0; i < cards.length - 1; i++) {
        tl.to(cards[i],     { opacity: 0,             ease: 'none', duration: 1 }, i);
        tl.to(cards[i + 1], { opacity: 1, y: 0,       ease: 'none', duration: 1 }, i);
    }

    // 2× the natural pin distance so each card transition unfolds across
    // more scroll — animation reads half as fast. pinSpacing inserts a
    // matching spacer, so case-2's pin still starts seamlessly where
    // case-1's ends, even without an endTrigger.
    const SPEED = 2;
    ScrollTrigger.create({
        ...pinDefaults,
        animation: tl,
        trigger: section,
        start: 'top top',
        end: () => '+=' + SPEED * window.innerHeight,
    });
}

// ─── CASE 2 ─────────────────────────────────────────────────────────────────
// Section pins; numbers orbit a shared arc so that at each step one number is
// "active" at 12 o'clock, with neighbours at 9 and 3 o'clock (±90°) and the
// rest invisibly staged at 6 o'clock. The matching card crossfades in sync
// with the active number reaching the centre.
const ARC = {
    prev:   -90,   // 9 o'clock
    active:   0,   // 12 o'clock (above the card)
    next:    90,   // 3 o'clock
    enter:  180,   // staged at 6 o'clock, invisible — rotates up into `next`
    exit:  -180,   // staged at 6 o'clock, invisible — `prev` rotates down here
};

function slotAt(i, step, N) {
    const slot = ((i - step) % N + N) % N;
    if (slot === 0)     return { angle: ARC.active, visible: true,  isActive: true  };
    if (slot === 1)     return { angle: ARC.next,   visible: true,  isActive: false };
    if (slot === N - 1) return { angle: ARC.prev,   visible: true,  isActive: false };
    if (slot === 2)     return { angle: ARC.enter,  visible: false, isActive: false };
    if (slot === N - 2) return { angle: ARC.exit,   visible: false, isActive: false };
    return                     { angle: 0,          visible: false, isActive: false };
}

function initCase2() {
    const section     = document.querySelector('.case-2');
    if (!section) return;

    const numbersBox  = section.querySelector('.case-2__numbers');
    const numbers     = gsap.utils.toArray('.case-2 .case-2__numbers__item');
    const cards       = gsap.utils.toArray('.case-2 .steps-container .step-item');
    const N           = numbers.length;

    if (!numbersBox || N < 2 || cards.length !== N) return;

    // Pill centre distance from the ring's outer edge — pills float outside
    // the ring so the arc line is still fully visible behind them.
    const PILL_RING_OFFSET = 80;
    // Gap beneath the side pills so they don't clip the section bottom edge.
    const BOTTOM_GAP = 16;
    // Ring sizing mirrors CSS `.case-2::after { width: 125vw; max-width: $cs-ring-max }`.
    // 125vw is read against the viewport (vw), not the section, so we use
    // `window.innerWidth` — the ring intentionally extends past the section
    // edges at narrower viewports.
    const RING_VW_FACTOR = 1.25;
    const RING_MAX       = 1000;

    let cx, cy, r, halfW, halfH;
    const measure = () => {
        const secRect   = section.getBoundingClientRect();
        const boxRect   = numbersBox.getBoundingClientRect();
        const itemRect  = numbers[0].getBoundingClientRect();
        // Dome lifted by `half-pill + gap` so the 9/3 pills clear the edge.
        const BOTTOM_LIFT = itemRect.height / 2 + BOTTOM_GAP;
        cx = secRect.left + secRect.width / 2 - boxRect.left;
        cy = secRect.top  + secRect.height - BOTTOM_LIFT - boxRect.top;
        const ringDiameter = Math.min(window.innerWidth * RING_VW_FACTOR, RING_MAX);
        const ringRadius   = ringDiameter / 2;
        r     = ringRadius + PILL_RING_OFFSET;
        halfW = itemRect.width  / 2;
        halfH = itemRect.height / 2;
    };

    const arcXY = (angleDeg) => {
        const t = (angleDeg * Math.PI) / 180;
        return {
            x: cx + r * Math.sin(t) - halfW,
            y: cy - r * Math.cos(t) - halfH,
        };
    };

    // One proxy per pill — shared across every transition that pill
    // participates in. Tracking the *current* angle in a single place means
    // `replaceAll()` can reposition every pill on resize regardless of which
    // tween (if any) is active at that moment.
    const itemProxies = numbers.map((_, i) => ({ angle: slotAt(i, 0, N).angle }));

    const placeItem = (i) => {
        const item  = numbers[i];
        const angle = itemProxies[i].angle;
        const p     = arcXY(angle);
        gsap.set(item, { x: p.x, y: p.y });
        // `.active` lives only while this pill is at 12 o'clock (angle ≈ 0).
        // As soon as it starts moving away the class comes off; it lands on
        // the next pill only when that one reaches centre. Direction-
        // agnostic — works the same on forward and reverse scrolls.
        item.classList.toggle('active', Math.abs(angle) < 0.5);
    };

    // Re-place every pill at its current tracked angle. Used on resize so
    // pills follow the new ring geometry even during holds (when no tween's
    // onUpdate is firing) and at progress 0 (when no tween has played yet).
    const replaceAll = () => {
        numbers.forEach((_, i) => placeItem(i));
    };

    measure();
    replaceAll();

    // Each step gets a `HOLD` window where the active pill rests at 12 o'clock
    // before the next `STEP` window rotates the ring. With `scrub: true` the
    // timeline is just a progress map: holds become scroll stretches where the
    // active number is visibly fixed at centre, transitions become the
    // stretches where the ring rotates. Pad the timeline with a final hold so
    // the last number also rests at centre at the end of the section.
    const HOLD = 1;
    const STEP = 1;
    const SLOT = HOLD + STEP;

    // The timeline owns the initial state via `tl.set` calls at position 0,
    // not external `gsap.set` calls — this way `invalidate()` on resize and
    // any replay seek both restore the step-0 baseline before chaining
    // through the transitions, regardless of where the user is in the scroll.
    // Plain `to` tweens (not `fromTo`) chain naturally from the shared proxy
    // and avoid `fromTo`'s `immediateRender` last-wins override of the
    // initial state.
    const tl = gsap.timeline();
    numbers.forEach((item, i) => {
        const s = slotAt(i, 0, N);
        tl.set(itemProxies[i], { angle: s.angle }, 0);
        tl.set(item, { opacity: s.visible ? 1 : 0 }, 0);
    });
    tl.set(cards[0], { opacity: 1 }, 0);
    tl.set(cards.slice(1), { opacity: 0 }, 0);

    for (let k = 0; k < N - 1; k++) {
        const start = k * SLOT + HOLD;
        numbers.forEach((item, i) => {
            const to = slotAt(i, k + 1, N);

            tl.to(itemProxies[i], {
                angle: to.angle,
                duration: STEP,
                ease: 'none',
                onUpdate: () => placeItem(i),
            }, start);
            tl.to(item, {
                opacity: to.visible ? 1 : 0,
                duration: STEP,
                ease: 'none',
            }, start);
        });
        tl.to(cards[k],     { opacity: 0, duration: 0.3, ease: 'none' }, start);
        tl.to(cards[k + 1], { opacity: 1, duration: 0.3, ease: 'none' }, start);
    }
    // Trailing hold so number N stays centred for the same scroll stretch as
    // every other active pill instead of clipping to the timeline end.
    tl.to({}, { duration: HOLD }, (N - 1) * SLOT + STEP);

    ScrollTrigger.create({
        ...pinDefaults,
        animation: tl,
        trigger: section,
        start: 'top top',
        ...endFor('.case-3', N - 1),
        onRefreshInit: () => {
            measure();
            // Re-place pills at their current tracked angles with the new
            // geometry. The subsequent timeline replay (triggered by
            // `invalidateOnRefresh`) chains positions forward from the
            // step-0 baseline set on the timeline; pills sitting in a hold
            // (no active tween) keep the geometry-corrected positions set
            // here.
            replaceAll();
        },
    });

    // ScrollTrigger debounces its own resize → refresh path (~200ms). With
    // `pinType: 'fixed'`, the pinned section is locked to its pre-resize
    // inline width during that window — so the ring (at `left: 50%` of
    // the section) is offset relative to the new viewport, and the pills
    // (which track section centre) appear stuck off-screen. Forcing a
    // rAF-throttled refresh updates the pinned width every frame, dragging
    // the ring + pills back in sync. `onRefreshInit` already calls
    // `measure()` + `replaceAll()`, so we don't repeat it here.
    let resizeRAF = 0;
    const onResize = () => {
        if (resizeRAF) cancelAnimationFrame(resizeRAF);
        resizeRAF = requestAnimationFrame(() => {
            resizeRAF = 0;
            ScrollTrigger.refresh();
        });
    };
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
        if (resizeRAF) cancelAnimationFrame(resizeRAF);
        window.removeEventListener('resize', onResize);
    };
}

// ─── CASE 3 ─────────────────────────────────────────────────────────────────
// Section pins; the inner steps row translates left by exactly the amount
// needed to reveal the last card, then the section releases.
function initCase3() {
    const section = document.querySelector('.case-3');
    if (!section) return;

    const track = section.querySelector('.steps-container');
    if (!track) return;

    // Stop with the last card's right edge 60px inside the `.case-3__inner`
    // (1400px) right edge — 40px breathing room + 20px to clear the wrapper
    // padding. Measured against `inner.offsetWidth` (immune to GSAP transforms)
    // so it stays stable mid-scroll.
    const TRAILING_GAP = 60;
    const inner = track.parentElement;
    const distance = () =>
        Math.max(0, track.scrollWidth - inner.offsetWidth + TRAILING_GAP);

    const tl = gsap.timeline();
    tl.to(track, { x: () => -distance(), ease: 'none', duration: 1 });

    ScrollTrigger.create({
        ...pinDefaults,
        animation: tl,
        trigger: section,
        start: 'top top',
        end: () => '+=' + distance(),
    });
}

// ─── MOBILE ─────────────────────────────────────────────────────────────────
// Simple IntersectionObserver-driven fade-in-up per `.step-item`. The matching
// CSS lives behind the same `max-width: 767px` guard, so this branch owns
// mobile entirely — no pinning, no orbit, just one-by-one card reveals.
function initMobileReveal() {
    const items = Array.from(document.querySelectorAll('.cs-pin-section .step-item'));
    if (!items.length) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
        items.forEach((item) => item.classList.add('is-visible'));
        return () => items.forEach((item) => item.classList.remove('is-visible'));
    }

    const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
        });
    }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

    items.forEach((item) => observer.observe(item));

    return () => {
        observer.disconnect();
        items.forEach((item) => item.classList.remove('is-visible'));
    };
}

// ─── BOOTSTRAP ───────────────────────────────────────────────────────────────
// Wait for full `load` so Elementor has finished laying out. `gsap.matchMedia`
// swaps the desktop (pinned GSAP) and mobile (fade-in-up) branches on resize,
// reverting any inline styles / ScrollTriggers from the previous branch.
function bootstrap() {
    const mm = gsap.matchMedia();

    mm.add('(min-width: 768px)', () => {
        initCase1();
        const cleanupCase2 = initCase2();
        initCase3();
        ScrollTrigger.refresh();
        return () => {
            if (cleanupCase2) cleanupCase2();
        };
    });

    mm.add('(max-width: 767px)', () => initMobileReveal());
}

if (document.readyState === 'complete') {
    bootstrap();
} else {
    window.addEventListener('load', bootstrap, { once: true });
}
